import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import "./FlashscanPage.scss";
import {
  FlashscanJobDto,
  enableFlashscanJobEmailNotification,
  getActiveFlashscanJob,
  getFlashscanJob,
  getLatestFlashscan,
  performFlashScan,
} from "@/api/flashscan";
import { addTestData } from "@/api/bills";
import { getCurrentUser } from "@/api/user";
import { FlashscanResult } from "@/types/Flashscan";
import { User } from "@/types/User";
import { FlashscanKPI } from "@/components/FlashscanKPI/FlashscanKPI";
import { FlashscanRiskBreakdown } from "@/components/FlashscanRiskBreakdown/FlashscanRiskBreakdown";
import { FlashscanAnomaliesTable } from "@/components/FlashscanAnomaliesTable/FlashscanAnomaliesTable";
import { GenericButton } from "@/components/GenericButton/GenericButton";
import { Loader } from "@/components/Loader/Loader";

export const FlashscanPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [latest, setLatest] = useState<FlashscanResult | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isRelaunchLoading, setIsRelaunchLoading] = useState(false);
  const [job, setJob] = useState<FlashscanJobDto | null>(null);
  const [isNotifyLoading, setIsNotifyLoading] = useState(false);
  const [isAddTestDataLoading, setIsAddTestDataLoading] = useState(false);
  const [testDataInfoMessage, setTestDataInfoMessage] = useState<string | null>(
    null,
  );
  const [testDataErrorMessage, setTestDataErrorMessage] = useState<
    string | null
  >(null);
  const isTestModeEnabled =
    process.env.REACT_APP_IS_TEST_MODE_ENABLED === "true";

  const stopLoaderWithDelay = () => {
    setTimeout(() => setIsLoading(false), 500);
  };

  const refreshAll = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const [u, l, activeJob] = await Promise.all([
        getCurrentUser(),
        getLatestFlashscan(),
        getActiveFlashscanJob(),
      ]);
      setUser(u);
      setLatest(l.data);
      setJob(activeJob.data);
    } catch {
      setHasError(true);
    } finally {
      stopLoaderWithDelay();
    }
  }, []);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const onRelaunch = async () => {
    setIsRelaunchLoading(true);
    setHasError(false);
    try {
      const created = await performFlashScan();
      setJob(created.data);
    } catch {
      setHasError(true);
    } finally {
      setIsRelaunchLoading(false);
    }
  };

  const onNotifyByEmail = async () => {
    if (!job) return;
    setIsNotifyLoading(true);
    try {
      const updated = await enableFlashscanJobEmailNotification(job.id);
      setJob(updated.data);
    } finally {
      setIsNotifyLoading(false);
    }
  };

  useEffect(() => {
    if (!job) return;
    if (job.status === "SUCCEEDED" || job.status === "FAILED") return;

    const interval = window.setInterval(async () => {
      try {
        const refreshed = await getFlashscanJob(job.id);
        setJob(refreshed.data);
        if (refreshed.data.status === "SUCCEEDED") {
          const res = await getLatestFlashscan();
          setLatest(res.data);
        }
      } catch {
        // best-effort polling; UI will still allow refresh/retry
      }
    }, 2000);

    return () => window.clearInterval(interval);
  }, [job]);

  const onAddTestData = async () => {
    setIsAddTestDataLoading(true);
    setTestDataInfoMessage(null);
    setTestDataErrorMessage(null);
    setIsLoading(true);
    setHasError(false);
    try {
      await addTestData();
      setTestDataInfoMessage(t("pages.flashscan.testData.success"));
      const res = await getLatestFlashscan();
      setLatest(res.data);
    } catch {
      setTestDataErrorMessage(t("pages.flashscan.testData.error"));
      setHasError(true);
    } finally {
      setIsAddTestDataLoading(false);
      stopLoaderWithDelay();
    }
  };

  const isConnectedToProviders =
    user != null && user.canPerformFlashScan === true;
  const isJobInProgress =
    job?.status === "PENDING" || job?.status === "RUNNING";
  const canNotifyByEmail =
    isConnectedToProviders &&
    isJobInProgress &&
    job != null &&
    job.notifyByEmail !== true;

  if (isLoading) {
    return (
      <div className="flashscan-page">
        <Loader />
      </div>
    );
  }

  if (hasError) {
    return <div className="flashscan-page">{t("errors.generic")}</div>;
  }

  return (
    <div className="flashscan-page">
      {!isJobInProgress &&
        (isConnectedToProviders ? (
          <div className="flashscan-page-header">
            {latest?.performedAt && (
              <div className="flashscan-page-last-run">
                {t("pages.flashscan.lastRun", {
                  date: new Date(latest.performedAt).toLocaleString("fr-FR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                })}
              </div>
            )}
            <div className="flashscan-page-actions">
              {isTestModeEnabled && (
                <GenericButton
                  label={
                    isAddTestDataLoading
                      ? t("pages.flashscan.actions.loading")
                      : t("pages.flashscan.actions.addTestData")
                  }
                  onClick={onAddTestData}
                  disabled={isAddTestDataLoading || isRelaunchLoading}
                  theme="secondary"
                />
              )}
              <GenericButton
                label={
                  isRelaunchLoading
                    ? t("pages.flashscan.actions.loading")
                    : t("pages.flashscan.actions.perform")
                }
                onClick={onRelaunch}
                disabled={
                  isRelaunchLoading || isAddTestDataLoading || isJobInProgress
                }
                theme="primary"
              />
            </div>
          </div>
        ) : (
          <div className="flashscan-page-not-connected">
            <div className="flashscan-page-not-connected-text">
              {t("pages.flashscan.notConnected")}
            </div>
            <div className="flashscan-page-not-connected-actions">
              <GenericButton
                label={t("pages.flashscan.goToIntegrations")}
                onClick={() => navigate("/integrations")}
                theme="primary"
              />
            </div>
          </div>
        ))}

      {isConnectedToProviders && job && isJobInProgress && (
        <div className="flashscan-page-job-status">
          {job.status === "PENDING" && (
            <p>{t("pages.flashscan.job.pending")}</p>
          )}
          {job.status === "RUNNING" && (
            <p>{t("pages.flashscan.job.running")}</p>
          )}

          {canNotifyByEmail && (
            <GenericButton
              label={
                isNotifyLoading
                  ? t("pages.flashscan.actions.loading")
                  : t("pages.flashscan.notify.cta")
              }
              onClick={onNotifyByEmail}
              disabled={isNotifyLoading}
              theme="secondary"
            />
          )}

          {job.notifyByEmail === true && (
            <p>{t("pages.flashscan.notify.confirmed")}</p>
          )}
        </div>
      )}

      {isConnectedToProviders && job && job.status === "FAILED" && (
        <div className="flashscan-page-job-status">
          <p>{t("pages.flashscan.job.failed")}</p>
        </div>
      )}

      {isTestModeEnabled && (
        <div className="flashscan-page-testdata-messages">
          {testDataInfoMessage && (
            <p className="flashscan-page-testdata-info">
              {testDataInfoMessage}
            </p>
          )}
          {testDataErrorMessage && (
            <p className="flashscan-page-testdata-error">
              {testDataErrorMessage}
            </p>
          )}
          {isAddTestDataLoading && (
            <p>{t("pages.flashscan.actions.loading")}</p>
          )}
        </div>
      )}

      {isConnectedToProviders && latest && (
        <FlashscanKPI flashscanResult={latest} />
      )}
      {isConnectedToProviders && latest && (
        <FlashscanRiskBreakdown flashscanResult={latest} />
      )}
      {isConnectedToProviders && latest && (
        <FlashscanAnomaliesTable flashscanResult={latest} onResolved={refreshAll} />
      )}
    </div>
  );
};
